'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { MainShopProduct } from '@/core/models/shop-product-model';
import { Settings } from '@/core/models/settings-model';
import { formatPrice } from "@/utils/formatUtils"
import { Heading } from '../../../components/ui/Heading';
import { Badge } from '../../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { getFabricInclusiveFinalPriceRial, getFabricInclusivePriceRial, getFabricPriceLabel } from '../utils/productPriceExcerpt';

type ProductCardProps = {
  product: MainShopProduct;
  settings?: Pick<Settings, 'fabric_rate_number' | 'fabric_rate_words' | 'vat_percentage' | 'discount_display_active'>;
  // فقط چندتای اول یه گرید باید priority بگیرن (برای LCP)؛ وگرنه با priority
  // رو همه‌ی تصاویر یه صفحه‌ی ۲۰-محصولی، مرورگر همه‌شون رو با اولویت بالا
  // preload می‌کنه که خودش سرعت لود صفحه رو بدتر می‌کنه.
  priority?: boolean;
};

export default function ProductCard({ product, settings, priority = false }: ProductCardProps) {
  const { dictionary } = useDictionary();
  const inStock = product.inventory && product.inventory > 0;
  const hasDiscount = product.discount_percent > 0;
  // وقتی این تنظیم غیرفعاله، برچسب تخفیف و قیمت خط‌خورده نشون داده نمی‌شه؛
  // فقط قیمت نهایی (که خودش شامل تخفیفه) به‌عنوان تنها قیمت نمایش داده می‌شه.
  const showDiscount = settings?.discount_display_active !== 'deactive';
  // اگه محصول متراژ پارچه داشته باشه و نرخ پارچه تو تنظیمات ثبت شده باشه،
  // قیمتی که به مشتری نشون داده می‌شه (نه قیمت خام بک‌اند) همون قیمتِ
  // با‌احتساب‌پارچه‌ست؛ دیگه لازم نیست ادمین این عدد رو تو excerpt دستی بنویسه.
  const fabricInclusivePrice = getFabricInclusivePriceRial(product, settings);
  const displayFinalPrice = getFabricInclusiveFinalPriceRial(product, settings);
  const fabricPriceLabel = getFabricPriceLabel(product, settings);


  return (
    <Link
      href={`/shop/product/${product.id}/${product.slug}`}
      className="p-0 flex flex-col"
    // style={{
    //   borderImageSlice: '4 4 4 4',
    //   borderImageWidth: '4px 4px 4px 4px',
    //   borderImageOutset: '.5px .5px .5px .5px',
    //   borderImageRepeat: 'round round',
    //   borderImageSource: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB3SURBVHgB7dVBCoAgFEXR911JjcRVVSurXamj2on5wiCkWd9RnpkiF0FRCSEkZCKyWWsXPHjvhzy/o5JSGp1zR7V2y2una3BHtbBnuEMo0u51Ba8NFLFn3u7hF+wZNPD3KB8HKNLudUWMcYUi9nj6MxSxJy3+qBOdU0PNJhRu4gAAAABJRU5ErkJggg==)',
    //   borderStyle: 'solid',
    //   imageRendering: 'pixelated'
    // }}
    >
      <div className="relative aspect-square overflow-hidden bg-primary-0">
        <Image
          src={product.thumbnail.url}
          alt={product.title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
          className="object-contain"
          priority={priority}
        />
        {hasDiscount && showDiscount
          && <Badge variant="destructive" className="absolute top-2 right-2 ltr:left-2 text-xs font-semibold rounded-full px-2.5 py-1 shadow-sm">
            {product.discount_percent}% {dictionary.ui?.cart.discount ?? 'تخفیف'}
          </Badge>}
        {!inStock && process.env.NEXT_PUBLIC_SITE_NAME === 'tolica_front'
          ? <Badge variant="secondary" className="absolute top-2 left-2 ltr:right-2 text-xs rounded-xs">
            {dictionary.ui?.product.noInventory}
          </Badge> : ""}

      </div>

      <div className="pt-2 space-y-2">
        <div className='flex justify-between items-center'>

          <Heading level={3} className="text-xs">
            {product.title}
          </Heading>
          <div className="space-y-1">
            {Number(product.price) === 0 || Number(product.final_price) === 0 ? (
              <p className="text-xs font-medium text-blue-600">{dictionary.ui.form.contactus}</p>
            ) :
              hasDiscount && showDiscount ? (
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-[11px] text-primary-400 line-through decoration-primary-400/70 whitespace-nowrap">
                    {formatPrice(fabricInclusivePrice)} {dictionary.ui.product.currency}
                  </span>
                  <span className="text-sm font-bold text-primary-700 whitespace-nowrap">
                    {formatPrice(displayFinalPrice)} {dictionary.ui.product.currency}
                  </span>
                </div>
              ) : (
                <span className="text-primary-600 text-xs whitespace-nowrap">
                  {formatPrice(fabricInclusivePrice)} {dictionary.ui.product.currency}
                </span>
              )}
          </div>
        </div>

        {fabricPriceLabel && (
          <p className="text-[11px] text-primary-500">{fabricPriceLabel}</p>
        )}

        {!!product.product_colors && <div className="flex flex-wrap gap-3">
          {product.product_colors.map((color, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <span>
                  <div
                    className="size-4 rounded-full border border-primary-300 cursor-pointer hover:ring-2 hover:ring-[#C6B8A3] transition-transform hover:scale-110"
                    style={{ backgroundColor: color.color_code_hex }}
                    title={color.color_title}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm text-primary-0">{color.color_title}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>}
      </div>
    </Link >
  );
}
